import React, { useMemo, useState } from "react";
import KpiOverview from "./KpiOverview";
import {
  formatDecimal,
  formatPercent,
  getAchievementToneClass,
  getAchievementPillClass,
  getVisitComparisonPillClass,
  formatCurrency,
  formatNumericUnit,
} from "../utils/formatters";
import PaginationControls from "./PaginationControls";

function SectionTitle({ children }) {
  return (
    <div className="section-title">
      <span>{children}</span>
    </div>
  );
}

function getBrandStatusIcon(value) {
  if (value >= 100) return "  ";
  if (value >= 80)  return "   ";
  return " ";
}

function renderCellByKind(value, kind, unit) {
  if (kind === "percent") {
    return <span className={getAchievementToneClass(value)}>{formatPercent(value)}</span>;
  }
  if (kind === "decimal")  return formatDecimal(value);
  if (kind === "currency") return formatCurrency(value, unit);
  return value;
}

// ── Helper: derive summaryCards from either shape the backend may return ──
function buildSummaryCards(monthlyCoverageTrend) {
  if (!monthlyCoverageTrend) return [];

  // New shape: { summary: { avgVisits, avgCoverage, latestCoverage }, … }
  if (monthlyCoverageTrend.summary) {
    const s = monthlyCoverageTrend.summary;
    return [
      { label: "Avg Visits / Month",    value: s.avgVisits      ?? 0 },
      { label: "Average Coverage",      value: s.avgCoverage    ?? 0 },
      { label: "Latest Month Coverage", value: s.latestCoverage ?? 0 },
    ];
  }

  // Old shape: { summaryCards: [{ label, value }] }
  if (Array.isArray(monthlyCoverageTrend.summaryCards)) {
    return monthlyCoverageTrend.summaryCards;
  }

  return [];
}

export default function EmployeeDetailPanel({
  loading,
  error,
  data,
  unit,
  sections,
  onSectionPageChange,
}) {
  const [activeTab, setActiveTab] = useState("visits");
  const [showWeekly] = useState(true);

  // ── Monthly coverage trend ────────────────────────────────────────────────
  const monthlySummaryCards = useMemo(
    () => buildSummaryCards(data?.monthlyCoverageTrend),
    [data?.monthlyCoverageTrend]
  );

  const monthlyData = useMemo(() => {
    const rows = data?.monthlyCoverageTrend?.rows;
    if (!Array.isArray(rows) || !rows.length) return [];

    return [
      { metric: "Total Visits",    kind: "decimal",  values: rows.map((r) => r.totalVisits) },
      { metric: "Total Doctors",   kind: "decimal",  values: rows.map((r) => r.totalDoctors) },
      { metric: "Doctors Covered", kind: "decimal",  values: rows.map((r) => r.doctorsCovered) },
      { metric: "Coverage %",      kind: "percent",  values: rows.map((r) => r.coveragePercent) },
      { metric: "Visits / Doctor", kind: "decimal",  values: rows.map((r) => r.visitsPerDoctor) },
    ];
  }, [data?.monthlyCoverageTrend]);

  // ── Weekly performance ────────────────────────────────────────────────────
  const weeklyData = useMemo(() => {
    const wd = data?.weeklyPerformance;
    if (!wd?.rows?.length || !wd?.months?.length) return [];

    return wd.months.map((month, monthIndex) => ({
      label: month,
      weeks: wd.rows.map((row) => {
        const cell = row[month] || {};

        let incremental = 0;
        if (monthIndex < wd.months.length - 1) {
          const prevMonth = wd.months[monthIndex + 1];
          const prevCell  = row[prevMonth] || {};
          const current   = cell.totalVisits ?? 0;
          const previous  = prevCell.totalVisits ?? 0;
          if (previous !== 0) incremental = ((current - previous) / previous) * 100;
        }

        return {
          week:               row.week,
          totalVisits:        cell.totalVisits     ?? 0,
          doctorCoverage:     cell.doctorCoverage  ?? 0,
          vfCompliance:       cell.vfCompliance    ?? 0,
          incrementalPercent: incremental,
        };
      }),
    }));
  }, [data?.weeklyPerformance]);

  const weekLabels = useMemo(
    () => weeklyData[0]?.weeks?.map((w) => w.week) || [],
    [weeklyData]
  );

  // ── Section state helpers ─────────────────────────────────────────────────
  const showGlobalLoading = Boolean(loading) && !sections;

  if (showGlobalLoading) {
    return (
      <section className="panel detail-panel">
        <div className="section-loader">Loading employee details...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="panel detail-panel">
        <div className="section-error">{error}</div>
      </section>
    );
  }

  if (!data && !sections) return null;

  const sectionState = (key) => {
    const s = sections?.[key];
    return s || { loading: false, error: "", loaded: false };
  };

  const renderSectionStatus = (key, emptyMessage = "No data available.") => {
    const s = sectionState(key);
    if (s.loading) {
      return (
        <div className="section-loader">
          <span className="loader-spinner" aria-hidden="true" />
          <span className="loader-text">
            Loading<span className="loader-dots" aria-hidden="true" />
          </span>
        </div>
      );
    }
    if (s.error)  return <div className="section-error">{s.error}</div>;
    if (s.loaded) return <div className="empty-state">{emptyMessage}</div>;
    return null;
  };

  const brandState          = sectionState("brandPerformance");
  const missedDoctorsState  = sectionState("missedDoctors");
  const patchCoverageState  = sectionState("patchCoverage");
  const specialtyCoverageState = sectionState("specialtyCoverage");

  return (
    <section className="panel detail-panel">
      {/* ── Meta + stat cards ─────────────────────────────────────────────── */}
      <div className="detail-top-grid">
        <div className="detail-stat-grid" />

        <div className="detail-stat-grid">
          {[
            { label: "Rep Name",  value: data?.repName    || "—" },
            { label: "Division",  value: data?.division   || "—" },
            { label: "Territory (HQ)", value: data?.territory || "—" },
            { label: "Month",     value: data?.monthLabel || "—" },
          ].map(({ label, value }) => (
            <div className="detail-meta-item" key={label}>
              <div className="detail-meta-label">{label}</div>
              <div className="detail-meta-value">{value}</div>
            </div>
          ))}
        </div>

        <div className="detail-stat-grid">
          <div className="detail-stat">
            <div className="detail-stat-label">Employee MTD Sales</div>
            <div className="detail-stat-value">{formatCurrency(data?.mtdSalesLakh ?? 0, unit)}</div>
          </div>
          <div className="detail-stat">
            <div className="detail-stat-label">Employee MTD Target</div>
            <div className="detail-stat-value">{formatCurrency(data?.mtdTargetLakh ?? 0, unit)}</div>
          </div>
          <div className="detail-stat">
            <div className="detail-stat-label">Employee MTD % Achievement</div>
            <div className={`detail-stat-value ${getAchievementToneClass(data?.achievement ?? 0)}`}>
              {formatPercent(data?.achievement ?? 0)}
            </div>
          </div>
          <div className="detail-stat">
            <div className="detail-stat-label">PPM</div>
            <div className="detail-stat-value">{formatNumericUnit(data?.ppm ?? 0, unit)}</div>
          </div>
        </div>
      </div>

      {/* ── Brand Performance ─────────────────────────────────────────────── */}
      <SectionTitle>Brand Performance</SectionTitle>

      {!brandState.loading && Array.isArray(data?.brandPerformance) && data.brandPerformance.length ? (
        <div className="table-shell">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>MTD Sales</th>
                <th>MTD Target</th>
                <th>MTD % Achievement</th>
              </tr>
            </thead>
            <tbody>
              {data.brandPerformance.map((row) => (
                <tr key={row.brand}>
                  <td>
                    <span className="brand-row">
                      <span className="brand-icon">{getBrandStatusIcon(row.achievement)}</span>
                      <span>{row.brand}</span>
                    </span>
                  </td>
                  <td>{formatCurrency(row.salesLakh, unit)}</td>
                  <td>{formatCurrency(row.targetLakh, unit)}</td>
                  <td>
                    <span className={getAchievementPillClass(row.achievement)}>
                      {formatPercent(row.achievement)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        renderSectionStatus("brandPerformance", "No brand performance data available.")
      )}

      <PaginationControls
        page={brandState.page}
        totalPages={brandState.totalPages}
        disabled={brandState.loading}
        onPageChange={(p) => onSectionPageChange?.("brandPerformance", p)}
      />

      {/* ── KPI Overview ──────────────────────────────────────────────────── */}
      <SectionTitle>KPIs Overview</SectionTitle>
      {Array.isArray(data?.kpiOverview) && data.kpiOverview.length ? (
        <KpiOverview items={data.kpiOverview} />
      ) : (
        renderSectionStatus("main", "No KPI data available.")
      )}

      {/* ── Missed Doctors ────────────────────────────────────────────────── */}
      <SectionTitle>Missed Doctors</SectionTitle>

      {!missedDoctorsState.loading && Array.isArray(data?.missedDoctors) && data.missedDoctors.length ? (
        <div className="table-shell">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Doctor Name</th>
                <th>Specialty</th>
                <th>Qualification</th>
                <th>City</th>
              </tr>
            </thead>
            <tbody>
              {data.missedDoctors.map((doctor, index) => (
                <tr key={`${doctor.name ?? doctor.doctorName ?? "doctor"}-${index}`}>
                  <td>{doctor.name ?? doctor.doctorName ?? "—"}</td>
                  <td>{doctor.specialty ?? doctor.speciality ?? "—"}</td>
                  <td>{doctor.qualification ?? "—"}</td>
                  <td>{doctor.city ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        renderSectionStatus("missedDoctors", "No missed doctors found.")
      )}

      <div className="pagination-center">
        <PaginationControls
          page={missedDoctorsState.page}
          totalPages={missedDoctorsState.totalPages}
          disabled={missedDoctorsState.loading}
          onPageChange={(p) => onSectionPageChange?.("missedDoctors", p)}
        />
      </div>

      {/* ── Patch Coverage ────────────────────────────────────────────────── */}
      <div className="patch-title-row">
        <div className="patch-title-center">
          <SectionTitle>Patch Coverage</SectionTitle>
        </div>

        <div className="visit-legend">
          <span className="coverage-pill purple">Actual &gt; Target</span>
          <span className="coverage-pill positive">Actual = Target</span>
          <span className="coverage-pill negative">Actual &lt; Target</span>
        </div>
      </div>
      {!patchCoverageState.loading && Array.isArray(data?.patchCoverage) && data.patchCoverage.length ? (
        <div className="table-shell">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Patch</th>
                <th>Doctors</th>
                <th>VF Target</th>
                <th>Actual Visits</th>
                <th>VF Adherence %</th>
              </tr>
            </thead>
            <tbody>
              {data.patchCoverage.map((row) => (
                <tr key={row.patch ?? row.patchName}>
                  <td>{row.patch ?? row.patchName ?? "—"}</td>
                  <td>{row.doctors ?? 0}</td>
                  <td>{row.vfTarget ?? 0}</td>
                  <td>
                    <span className={getVisitComparisonPillClass(row.actualVisits ?? 0, row.vfTarget ?? 0)}>
                      {row.actualVisits ?? 0}
                    </span>
                  </td>
                  <td>
                    <span className={getVisitComparisonPillClass(row.actualVisits ?? 0, row.vfTarget ?? 0)}>
                      {formatPercent(row.vfAdherence ?? 0)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        renderSectionStatus("patchCoverage", "No patch coverage data available.")
      )}

      <PaginationControls
        page={patchCoverageState.page}
        totalPages={patchCoverageState.totalPages}
        disabled={patchCoverageState.loading}
        onPageChange={(p) => onSectionPageChange?.("patchCoverage", p)}
      />

      {/* ── Specialty Coverage ────────────────────────────────────────────── */}
      {/* ✅ FIX: was reading snake_case (vf_target, actual_visits,           */}
      {/*   vf_adherence_percent) but dashboardApi normalises to camelCase.   */}
      <SectionTitle>Specialty Coverage - Rep Level</SectionTitle>

      {!specialtyCoverageState.loading && Array.isArray(data?.specialtyCoverage) && data.specialtyCoverage.length ? (
        <div className="table-shell">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Specialty</th>
                <th>Doctors</th>
                <th>VF Target</th>
                <th>Actual Visits</th>
                <th>VF Adherence %</th>
              </tr>
            </thead>
            <tbody>
              {data.specialtyCoverage.map((row, idx) => (
                // ✅ Use a stable key that doesn't depend on undefined fields
                <tr key={row.speciality ?? row.specialty ?? idx}>
                  <td>{row.speciality ?? row.specialty ?? "—"}</td>
                  <td>{row.doctors ?? 0}</td>
                  {/* ✅ FIX: was row.vf_target — now correctly row.vfTarget */}
                  <td>{row.vfTarget ?? 0}</td>
                  <td>
                    {/* ✅ FIX: was row.actual_visits — now row.actualVisits */}
                    <span className={getVisitComparisonPillClass(row.actualVisits ?? 0, row.vfTarget ?? 0)}>
                      {row.actualVisits ?? 0}
                    </span>
                  </td>
                  <td>
                    {/* ✅ FIX: was row.vf_adherence_percent — now row.vfAdherence */}
                    <span className={getVisitComparisonPillClass(row.actualVisits ?? 0, row.vfTarget ?? 0)}>
                      {formatPercent(row.vfAdherence ?? 0)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        renderSectionStatus("specialtyCoverage", "No specialty coverage data available.")
      )}

      <PaginationControls
        page={specialtyCoverageState.page}
        totalPages={specialtyCoverageState.totalPages}
        disabled={specialtyCoverageState.loading}
        onPageChange={(p) => onSectionPageChange?.("specialtyCoverage", p)}
      />

      {/* ── Monthly Coverage Trend ────────────────────────────────────────── */}
      {/* ✅ FIX: supports both new shape ({ summary, months, rows }) and      */}
      {/*   old shape ({ summaryCards, months, rows })                         */}
      <SectionTitle>Monthly Coverage Trend</SectionTitle>

      {monthlySummaryCards.length ? (
        <>
          <div className="monthly-summary-cards">
            {monthlySummaryCards.map((card) => (
              <div className="monthly-summary-card" key={card.label}>
                <div className="detail-stat-label">{card.label}</div>
                <div className="detail-stat-value">
                  {String(card.label).toLowerCase().includes("coverage")
                    ? formatPercent(Number(card.value))
                    : formatDecimal(Number(card.value))}
                </div>
              </div>
            ))}
          </div>

          <div className="table-shell">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {(data.monthlyCoverageTrend.months || []).map((month) => (
                    <th key={month}>{month}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((row) => (
                  <tr key={row.metric}>
                    <td>{row.metric}</td>
                    {row.values.map((value, index) => (
                      <td key={`${row.metric}-${index}`}>
                        {renderCellByKind(value, row.kind, unit)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        renderSectionStatus("monthlyCoverageTrend", "No monthly coverage trend available.")
      )}

      {/* ── Weekly Performance ────────────────────────────────────────────── */}
      <SectionTitle>Monthly &amp; Weekly Performance Overview</SectionTitle>

      <div className="weekly-tabs">
        {[
          { key: "visits",      label: "Total Visits",    note: "Total doctor visits made per week" },
          { key: "coverage",    label: "Coverage",        note: "% of assigned doctors visited at least once" },
          { key: "compliance",  label: "VF Compliance",   note: "% adherence to visit frequency targets" },
          { key: "incremental", label: "Incremental %",   note: "Week-over-week growth in visits" },
        ].map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? "tab active" : "tab"}
            onClick={() => setActiveTab(tab.key)}
            title={tab.note}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="weekly-tab-note">
        {activeTab === "visits" && "Total doctor visits made by the rep each week."}
        {activeTab === "coverage" && "Percentage of assigned doctors visited at least once during the week."}
        {activeTab === "compliance" && "Adherence to prescribed visit frequency (VF) targets."}
        {activeTab === "incremental" && "Week-over-week growth percentage in visits or coverage."}
      </div>

      {showWeekly && (
        data?.weeklyPerformance?.rows?.length ? (
          <div className="table-shell">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Week</th>
                  {weeklyData.map((month) => (
                    <th key={month.label}>{month.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekLabels.map((weekLabel) => (
                  <tr key={weekLabel}>
                    <td><span className="week-chip">{weekLabel}</span></td>
                    {weeklyData.map((month) => {
                      const item = month.weeks.find((w) => w.week === weekLabel);
                      let value = 0;
                      if (activeTab === "visits")      value = item?.totalVisits;
                      if (activeTab === "coverage")    value = item?.doctorCoverage;
                      if (activeTab === "compliance")  value = item?.vfCompliance;
                      if (activeTab === "incremental") value = item?.incrementalPercent;

                      return (
                        <td key={`${month.label}-${weekLabel}`}>
                          {activeTab === "visits" ? formatDecimal(value) : formatPercent(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          renderSectionStatus("weeklyPerformance", "No weekly performance available.")
        )
      )}
    </section>
  );
}